import { useCallback, useEffect, useState } from 'react';
import { differenceInDays, format, isToday, parseISO, startOfDay, subDays } from 'date-fns';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import type { LifeCheckPriority, LifeCheckSummary } from '@/types';
import { isTransientConnectionError, useNetworkStatus } from '../../ui-foundation/core';
import { isLifeCheckStale } from '../../ui-foundation/features/notifications';
import { buildBudgetCategoryCollections, hasBudgetCustomisation } from '../../ui-foundation/features/money';
import { buildWorkCashflowSnapshot } from '../../ui-foundation/features/work';
import { buildPetAttentionSurfaceItems } from '../../ui-foundation/features/pets';
import { getSharedHomeRoutineMeta } from '../../ui-foundation';
import { getQuickReminderAttention, isQuickReminderEvent } from '../../ui-foundation/features/notifications';

export interface AttentionItem {
  id: string;
  emoji: string;
  text: string;
  path: string;
  label: string;
  score: number;
}

const ATTENTION_CACHE_TTL_MS = 60 * 1000;

let attentionItemsCache:
  | {
      userId: string;
      items: AttentionItem[];
      loadedAt: number;
    }
  | null = null;

function topLifeCheckPriority(summary: unknown): LifeCheckPriority | null {
  const priorities = (summary as LifeCheckSummary | null)?.priorities;
  return Array.isArray(priorities) && priorities.length > 0 ? priorities[0] : null;
}

export function useAttentionItems() {
  const { user, data } = useApp();
  const { markConnectionHealthy, reportConnectionIssue } = useNetworkStatus();
  const [items, setItems] = useState<AttentionItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAttentionItems = useCallback(async () => {
    if (!user) {
      setItems([]);
      attentionItemsCache = null;
      markConnectionHealthy();
      setLoading(false);
      return;
    }

    const cached = attentionItemsCache?.userId === user.id ? attentionItemsCache : null;

    if (cached) {
      setItems(cached.items);
      setLoading(false);

      if (Date.now() - cached.loadedAt < ATTENTION_CACHE_TTL_MS) {
        markConnectionHealthy();
        return;
      }
    } else {
      setLoading(true);
    }

    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const hasChildren = data.profile.children.some((child) => child.name.trim().length > 0);
      const childProfileCount = data.profile.children.filter((child) =>
        child.name.trim().length > 0
        && Boolean(
          child.dateOfBirth
          || child.nhsNumber
          || child.gpSurgery
          || child.schoolName
          || (child.emergencyContacts || []).length
          || child.allergies
          || child.medications
          || child.notes
        )
      ).length;
      const adultDependantCount = (data.profile.adultDependants || []).length;
      const selfEmploymentActive = ['self-employed', 'mix'].includes(data.profile.workType);
      const hasFamilyContext = Boolean(
        data.profile.partnerName.trim()
        || data.profile.dependantTypes.length > 0
        || hasChildren,
      );
      const hasBudgetSetup = hasBudgetCustomisation(
        data.budgetLimits,
        data.profile.annualIncome || 0,
        data.budgetCategoryPreferences.length,
      );
      const hasLoggedExpense = data.expenses.some((expense) => !expense.isRecurring);
      const monthlyExpenses = data.expenses.filter((expense) => expense.date.startsWith(currentMonth));
      const visibleBudgetCategories = buildBudgetCategoryCollections({
        budgetLimitCategories: data.budgetLimits.map((limit) => limit.category),
        expenseCategories: data.expenses.map((expense) => expense.category),
        preferences: data.budgetCategoryPreferences,
      }).visible;
      const overspentCategory = data.budgetLimits
        .filter((limit) => visibleBudgetCategories.includes(limit.category))
        .map((limit) => ({
          category: limit.category,
          limit: limit.limit,
          spent: monthlyExpenses
            .filter((expense) => expense.category === limit.category)
            .reduce((sum, expense) => sum + expense.amount, 0),
        }))
        .find((entry) => entry.limit > 0 && entry.spent > entry.limit);

      const [
        benefitsResult,
        renewalsResult,
        petsResult,
        healthResult,
        petFoodProfilesResult,
        wellbeingResult,
        eventsResult,
        lifeCheckResult,
        homeProfileResult,
        documentRenewalsResult,
        homeAdminResult,
        homeRoutineResult,
        homeBillSettingsResult,
        familyAdminResult,
        playbookReminderResult,
        hmrcDeadlineResult,
        workDeadlineResult,
        businessExpensesResult,
        incomeLogResult,
        workInvoicesResult,
      ] = await Promise.all([
        supabase
          .from('benefits_viewed')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id),
        supabase
          .from('renewals')
          .select('id, provider, renewal_date, reminder_days_before')
          .eq('user_id', user.id)
          .order('renewal_date', { ascending: true }),
        supabase
          .from('pets')
          .select('id, name, pet_type')
          .order('name', { ascending: true }),
        supabase
          .from('health_reminders')
          .select('id, title, due_date, subject_type, subject_id')
          .eq('user_id', user.id)
          .is('completed_at', null)
          .order('due_date', { ascending: true, nullsFirst: false }),
        (supabase as any)
          .from('pet_food_profiles')
          .select('id, pet_id, brand_name, flavour, package_size, price, last_purchase_date, lasts_for_days, refill_reminder_days_before, notes')
          .order('created_at', { ascending: false }),
        supabase
          .from('wellbeing_checkins')
          .select('id, checked_in_at, mood')
          .eq('user_id', user.id)
          .gte('checked_in_at', subDays(new Date(), 7).toISOString())
          .order('checked_in_at', { ascending: false })
          .limit(7),
        supabase
          .from('events')
          .select('id, title, date, time, capture_kind, reminder_strength')
          .eq('user_id', user.id)
          .order('date', { ascending: true })
          .limit(12),
        supabase
          .from('life_checks')
          .select('id, created_at, summary')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('home_profiles')
          .select('id, address')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle(),
        supabase
          .from('document_renewals')
          .select('id, name, date')
          .eq('user_id', user.id)
          .order('date', { ascending: true }),
        supabase
          .from('home_maintenance_items' as any)
          .select('id, title, due_date, status')
          .eq('user_id', user.id)
          .neq('status', 'done')
          .order('due_date', { ascending: true, nullsFirst: false }),
        supabase
          .from('home_recurring_routines' as any)
          .select('id, title, due_date, status, routine_kind')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('due_date', { ascending: true, nullsFirst: false }),
        supabase
          .from('home_bill_settings')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('enabled', true),
        supabase
          .from('family_admin_items' as any)
          .select('id, title, due_date, status')
          .eq('user_id', user.id)
          .neq('status', 'done')
          .order('due_date', { ascending: true, nullsFirst: false }),
        supabase
          .from('playbook_reminders')
          .select('id, title, due_date, section, playbook_id')
          .eq('user_id', user.id)
          .order('due_date', { ascending: true }),
        supabase
          .from('hmrc_deadlines')
          .select('id, title, deadline_date')
          .eq('user_id', user.id)
          .eq('completed', false)
          .order('deadline_date', { ascending: true }),
        supabase
          .from('work_deadlines' as any)
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('completed', false),
        supabase
          .from('expenses')
          .select('amount, is_recurring, recurring_period')
          .eq('user_id', user.id)
          .eq('expense_scope', 'business'),
        supabase
          .from('income_log')
          .select('id, amount, received_date')
          .eq('user_id', user.id)
          .order('received_date', { ascending: false })
          .gte('received_date', format(subDays(new Date(), 180), 'yyyy-MM-dd')),
        supabase
          .from('work_invoices' as any)
          .select('id, invoice_number, client_name, due_date, amount, status')
          .eq('user_id', user.id)
          .neq('status', 'paid')
          .order('due_date', { ascending: true }),
      ]);

      const nextItems: AttentionItem[] = [];

    const monthlyBusinessRunningCost = ((businessExpensesResult.data || []) as any[]).reduce((sum, entry) => {
      if (!entry.is_recurring) return sum;
      if (entry.recurring_period === 'yearly') return sum + Number(entry.amount || 0) / 12;
      if (entry.recurring_period === 'monthly') return sum + Number(entry.amount || 0);
      return sum;
    }, 0);
    const workCashflow = selfEmploymentActive
      ? buildWorkCashflowSnapshot({
          incomeEntries: ((incomeLogResult.data || []) as any[]).map((entry) => ({
            amount: Number(entry.amount || 0),
            receivedDate: entry.received_date,
          })),
          invoices: ((workInvoicesResult.data || []) as any[]).map((invoice) => ({
            id: invoice.id,
            invoiceNumber: invoice.invoice_number || invoice.id,
            clientName: invoice.client_name,
            dueDate: invoice.due_date,
            amount: Number(invoice.amount || 0),
            status: invoice.status,
          })),
          monthlyRunningCost: monthlyBusinessRunningCost,
        })
      : null;

    const latestLifeCheck = lifeCheckResult.data;
    const latestPriority = latestLifeCheck ? topLifeCheckPriority(latestLifeCheck.summary) : null;
    if (!latestLifeCheck || isLifeCheckStale(latestLifeCheck.created_at)) {
      nextItems.push({
        id: 'life-check-stale',
        emoji: '🔔',
        text: 'Run your Life Check. It takes two minutes and resets the week.',
        path: '/life-check',
        label: 'Life Check',
        score: 100,
      });
    } else if (latestPriority) {
      nextItems.push({
        id: `life-check-${latestPriority.section}`,
        emoji: latestPriority.emoji,
        text: `Life Check says ${latestPriority.label} needs attention next.`,
        path: latestPriority.path,
        label: 'Life Check',
        score: 90,
      });
    }

    if (!hasBudgetSetup) {
      nextItems.push({
        id: 'budget-setup',
        emoji: '💰',
        text: 'Set your live budget so Money can keep spending and support in one place.',
        path: '/money?tab=budget',
        label: 'Budget setup',
        score: 82,
      });
    } else if (!hasLoggedExpense) {
      nextItems.push({
        id: 'expense-start',
        emoji: '💳',
        text: 'Log your first expense so Money reflects what is actually happening.',
        path: '/money?tab=budget',
        label: 'Spending',
        score: 66,
      });
    }

    if (overspentCategory) {
      nextItems.push({
        id: `budget-${overspentCategory.category}`,
        emoji: '💰',
        text: `${overspentCategory.category} is already over budget this month.`,
        path: '/money?tab=budget',
        label: 'Budget',
        score: 87,
      });
    }

    if (!benefitsResult.count || benefitsResult.count === 0) {
      nextItems.push({
        id: 'benefits-scan',
        emoji: '💰',
        text: 'Run your benefits scan. You have not checked support yet.',
        path: '/money',
        label: 'Benefits',
        score: 80,
      });
    }

    const hasHomeProfile = Boolean(homeProfileResult.data?.id && homeProfileResult.data?.address);
    if (!hasHomeProfile) {
      nextItems.push({
        id: 'home-profile',
        emoji: '🏠',
        text: 'Add your home profile so bills, housing help, and documents can line up properly.',
        path: '/home?tab=my-home',
        label: 'Home setup',
        score: 78,
      });
    } else if ((homeBillSettingsResult.count || 0) === 0) {
      nextItems.push({
        id: 'home-bills-start',
        emoji: '🏠',
        text: 'Switch on the household bills you want visible in Home.',
        path: '/home?tab=my-home&section=bills',
        label: 'Bills',
        score: 71,
      });
    }

    const urgentHomeItem = (homeAdminResult.data || []).find((item: any) => {
      if (!item.due_date) return false;
      return differenceInDays(parseISO(item.due_date), startOfDay(new Date())) <= 14;
    });

    if (urgentHomeItem) {
      const daysUntil = differenceInDays(parseISO(urgentHomeItem.due_date), startOfDay(new Date()));
      nextItems.push({
        id: `home-admin-${urgentHomeItem.id}`,
        emoji: '🧰',
        text:
          daysUntil < 0
            ? `${urgentHomeItem.title} is overdue.`
            : `${urgentHomeItem.title} needs attention in ${daysUntil} day${daysUntil === 1 ? '' : 's'}.`,
        path: '/home?tab=admin',
        label: 'Home admin',
        score: daysUntil < 0 ? 83 : 67,
      });
    } else if (hasHomeProfile && (!homeAdminResult.data || homeAdminResult.data.length === 0)) {
      nextItems.push({
        id: 'home-admin-start',
        emoji: '🧰',
        text: 'Start a repair, maintenance, or moving item so home admin has a real trail.',
        path: '/home?tab=admin',
        label: 'Home admin',
        score: 63,
      });
    }

    const urgentRenewal = (renewalsResult.data || []).find((renewal) => {
      const daysUntil = differenceInDays(parseISO(renewal.renewal_date), startOfDay(new Date()));
      return daysUntil <= renewal.reminder_days_before;
    });

    if (urgentRenewal) {
      const daysUntil = differenceInDays(parseISO(urgentRenewal.renewal_date), startOfDay(new Date()));
      nextItems.push({
        id: `renewal-${urgentRenewal.id}`,
        emoji: '🏠',
        text:
          daysUntil < 0
            ? `${urgentRenewal.provider} renewal is overdue.`
            : `${urgentRenewal.provider} renews in ${daysUntil} day${daysUntil === 1 ? '' : 's'}.`,
        path: '/home?tab=my-home&section=bills',
        label: 'Bills',
        score: daysUntil < 0 ? 88 : 72,
      });
    }

    const urgentHomeRoutine = (homeRoutineResult.data || []).find((routine: any) => {
      if (!routine.due_date) return false;
      return differenceInDays(parseISO(routine.due_date), startOfDay(new Date())) <= 7;
    });

    if (urgentHomeRoutine?.due_date) {
      const daysUntil = differenceInDays(parseISO(urgentHomeRoutine.due_date), startOfDay(new Date()));
      const routineMeta = getSharedHomeRoutineMeta(urgentHomeRoutine.routine_kind);
      nextItems.push({
        id: `home-routine-${urgentHomeRoutine.id}`,
        emoji: routineMeta.emoji,
        text:
          daysUntil < 0
            ? `${urgentHomeRoutine.title} is overdue.`
            : `${urgentHomeRoutine.title} is due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}.`,
        path: '/home?tab=my-home',
        label: 'Home routine',
        score: daysUntil < 0 ? 76 : 61,
      });
    }

    const documentRenewal = (documentRenewalsResult.data || []).find((renewal) => {
      const daysUntil = differenceInDays(parseISO(renewal.date), startOfDay(new Date()));
      return daysUntil <= 21;
    });

    if (documentRenewal) {
      const daysUntil = differenceInDays(parseISO(documentRenewal.date), startOfDay(new Date()));
      nextItems.push({
        id: `document-renewal-${documentRenewal.id}`,
        emoji: '📂',
        text:
          daysUntil < 0
            ? `${documentRenewal.name} renewal date has passed.`
            : `${documentRenewal.name} needs checking in ${daysUntil} day${daysUntil === 1 ? '' : 's'}.`,
        path: '/documents',
        label: 'Documents',
        score: daysUntil < 0 ? 85 : 69,
      });
    }

    const petAttentionItems = buildPetAttentionSurfaceItems({
      pets: ((petsResult.data || []) as any[]).map((pet) => ({
        id: pet.id,
        name: pet.name,
        petType: pet.pet_type,
      })),
      healthReminders: ((healthResult.data || []) as any[]).map((reminder) => ({
        id: reminder.id,
        title: reminder.title,
        dueDate: reminder.due_date,
        subjectType: reminder.subject_type,
        subjectId: reminder.subject_id,
      })),
      petFoodProfiles: ((petFoodProfilesResult.data || []) as any[]).map((profile) => ({
        id: profile.id,
        pet_id: profile.pet_id,
        brand_name: profile.brand_name,
        flavour: profile.flavour,
        package_size: profile.package_size,
        price: profile.price,
        last_purchase_date: profile.last_purchase_date,
        lasts_for_days: profile.lasts_for_days,
        refill_reminder_days_before: profile.refill_reminder_days_before,
        notes: profile.notes,
      })),
    });

    const urgentHealthReminder = (healthResult.data || []).find((reminder: any) => {
      if (reminder.subject_type === 'pet') return false;
      if (!reminder.due_date) return false;
      return differenceInDays(parseISO(reminder.due_date), startOfDay(new Date())) <= 14;
    });

    const missingHealthProfile =
      !data.profile.dateOfBirth
      || !data.profile.screeningInvitationPath
      || data.profile.hasCervix === null
      || data.profile.breastScreeningRelevant === null
      || data.profile.prostateScreeningRelevant === null;

    if (missingHealthProfile) {
      nextItems.push({
        id: 'health-profile',
        emoji: '🩺',
        text: 'Complete your health profile so screenings and reminders are actually relevant to you.',
        path: '/profile',
        label: 'Health setup',
        score: 74,
      });
    }

    if (urgentHealthReminder?.due_date) {
      const daysUntil = differenceInDays(parseISO(urgentHealthReminder.due_date), startOfDay(new Date()));
      nextItems.push({
        id: `health-${urgentHealthReminder.id}`,
        emoji: '🧠',
        text:
          daysUntil < 0
            ? `${urgentHealthReminder.title} is overdue.`
            : `${urgentHealthReminder.title} is due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}.`,
        path: '/health',
        label: 'Health',
        score: daysUntil < 0 ? 86 : 68,
      });
    }

    nextItems.push(...petAttentionItems);

    const wellbeingCheckins = wellbeingResult.data || [];
    const todayCheckin = wellbeingCheckins.find((entry) => isToday(new Date(entry.checked_in_at)));
    if (!todayCheckin || !isToday(new Date(todayCheckin.checked_in_at))) {
      nextItems.push({
        id: 'wellbeing-checkin',
        emoji: '🧠',
        text: 'Check in with yourself today before the day runs away with you.',
        path: '/health',
        label: 'Wellbeing',
        score: 60,
      });
    }

    const lowMoodCount = wellbeingCheckins.filter((entry) => entry.mood <= 2).length;
    if (lowMoodCount >= 2) {
      nextItems.push({
        id: 'burnout-watch',
        emoji: '🧠',
        text: 'Your recent check-ins suggest the load is building. Use wellbeing support before it snowballs.',
        path: '/health?tab=wellbeing',
        label: 'Burnout watch',
        score: 72,
      });
    }

    if (!hasFamilyContext) {
      nextItems.push({
        id: 'family-setup',
        emoji: '🫶',
        text: 'Save your partner, child, or dependant details so People has a real life to organise.',
        path: '/profile',
        label: 'People setup',
        score: 73,
      });
      } else if (childProfileCount + adultDependantCount === 0) {
        nextItems.push({
          id: 'family-profiles-detail',
          emoji: '🫶',
          text: 'Add school, GP, care, or emergency detail to one child or dependant profile.',
          path: '/profile',
        label: 'People profiles',
        score: 64,
      });
    }

    const reminderEvent = ((eventsResult.data || []) as any[])
      .filter((event) => isQuickReminderEvent(event))
      .map((event) => ({
        event,
        attention: getQuickReminderAttention(event),
      }))
      .filter((entry): entry is { event: any; attention: NonNullable<ReturnType<typeof getQuickReminderAttention>> } => Boolean(entry.attention))
      .sort((left, right) => right.attention.score - left.attention.score)[0];

    const nextEvent = ((eventsResult.data || []) as any[]).find((event) => {
      if (isQuickReminderEvent(event)) return false;
      const daysUntil = differenceInDays(parseISO(event.date), startOfDay(new Date()));
      return daysUntil >= 0 && daysUntil <= 7;
    });

    const urgentFamilyAdmin = (familyAdminResult.data || []).find((item: any) => {
      if (!item.due_date) return false;
      return differenceInDays(parseISO(item.due_date), startOfDay(new Date())) <= 14;
    });

    if (urgentFamilyAdmin) {
      const daysUntil = differenceInDays(parseISO(urgentFamilyAdmin.due_date), startOfDay(new Date()));
      nextItems.push({
        id: `family-admin-${urgentFamilyAdmin.id}`,
        emoji: '🧾',
        text:
          daysUntil < 0
            ? `${urgentFamilyAdmin.title} is overdue.`
            : `${urgentFamilyAdmin.title} is due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}.`,
        path: '/family?tab=calendar',
        label: 'Family admin',
        score: daysUntil < 0 ? 82 : 62,
      });
    } else if (hasChildren && (!familyAdminResult.data || familyAdminResult.data.length === 0)) {
      nextItems.push({
        id: 'family-admin-start',
        emoji: '🧾',
        text: 'Start a family admin item for school, childcare, or care planning.',
        path: '/family?tab=calendar',
        label: 'Family admin',
        score: 58,
      });
    }

    if (reminderEvent) {
      nextItems.push({
        id: `quick-reminder-${reminderEvent.event.id}`,
        emoji: '⏰',
        text: reminderEvent.attention.text,
        path: '/calendar',
        label: 'Reminder',
        score: reminderEvent.attention.score,
      });
    }

    const visibleFamilyEvents = ((eventsResult.data || []) as any[]).filter((event) => !isQuickReminderEvent(event));
    if (hasChildren && visibleFamilyEvents.length === 0) {
      nextItems.push({
        id: 'family-empty',
        emoji: '👨‍👩‍👧',
        text: 'Add a family event or school date so the week is not living in your head.',
        path: '/family',
        label: 'People',
        score: 59,
      });
    }

    const urgentPlaybookReminder = (playbookReminderResult.data || []).find((item: any) => {
      const daysUntil = differenceInDays(parseISO(item.due_date), startOfDay(new Date()));
      return daysUntil <= 14;
    });

    if (urgentPlaybookReminder) {
      const daysUntil = differenceInDays(parseISO(urgentPlaybookReminder.due_date), startOfDay(new Date()));
      nextItems.push({
        id: `playbook-reminder-${urgentPlaybookReminder.id}`,
        emoji: '📖',
        text:
          daysUntil < 0
            ? `${urgentPlaybookReminder.title} is overdue.`
            : `${urgentPlaybookReminder.title} is due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}.`,
        path: `/guides/${urgentPlaybookReminder.section}/playbook/${urgentPlaybookReminder.playbook_id}`,
        label: 'Guide follow-through',
        score: daysUntil < 0 ? 83 : 65,
      });
    }

    const overdueInvoice = (workInvoicesResult.data || []).find((invoice: any) =>
      differenceInDays(parseISO(invoice.due_date), startOfDay(new Date())) < 0
    );

    if (overdueInvoice) {
      nextItems.push({
        id: `invoice-${overdueInvoice.id}`,
        emoji: '🧾',
        text: `${overdueInvoice.client_name} is overdue. Chase it before it drifts further.`,
        path: '/work?tab=self-employed',
        label: 'Invoices',
        score: 81,
      });
    } else if (selfEmploymentActive && (!workInvoicesResult.data || workInvoicesResult.data.length === 0)) {
      nextItems.push({
        id: 'invoice-start',
        emoji: '🧾',
        text: 'Add your first invoice so money owed to you is tracked in Adulting too.',
        path: '/work?tab=self-employed',
        label: 'Invoices',
        score: 61,
      });
    }

    if (selfEmploymentActive && workCashflow?.lowMonthAlert) {
      nextItems.push({
        id: 'work-low-month',
        emoji: '🛟',
        text: workCashflow.lowMonthAlert.summary,
        path: '/money?tab=survival',
        label: 'Survival',
        score: workCashflow.lowMonthAlert.level === 'high' ? 85 : 74,
      });
    } else if (selfEmploymentActive && workCashflow?.currentMonthAlert) {
      nextItems.push({
        id: 'work-low-month-watch',
        emoji: '🛟',
        text: workCashflow.currentMonthAlert.summary,
        path: '/money?tab=survival',
        label: 'Survival',
        score: workCashflow.currentMonthAlert.level === 'high' ? 79 : 70,
      });
    }

    if (nextEvent) {
      const daysUntil = differenceInDays(parseISO(nextEvent.date), startOfDay(new Date()));
      nextItems.push({
        id: `event-${nextEvent.id}`,
        emoji: '👨‍👩‍👧',
        text: `${nextEvent.title} is coming up in ${daysUntil} day${daysUntil === 1 ? '' : 's'}.`,
        path: '/family',
        label: 'People',
        score: 55,
      });
    }

    const nextHmrcDeadline = hmrcDeadlineResult.data?.[0];
    const pendingWorkDeadlineCount = workDeadlineResult.count || 0;
    const hasWorkSetup = Boolean(data.profile.employmentType || data.profile.workType);
    if (!hasWorkSetup) {
      nextItems.push({
        id: 'work-setup',
        emoji: '💼',
        text: 'Save your work setup so Adulting can show the right deadlines, rights, and tools.',
        path: '/profile',
        label: 'Work setup',
        score: 71,
      });
    }

    if (selfEmploymentActive && !nextHmrcDeadline) {
      nextItems.push({
        id: 'hmrc-setup',
        emoji: '💼',
        text: 'Set up your HMRC deadlines so work admin does not ambush you later.',
        path: '/work?tab=self-employed',
        label: 'Work',
        score: 66,
      });
    } else if (nextHmrcDeadline) {
      const daysUntil = differenceInDays(parseISO(nextHmrcDeadline.deadline_date), startOfDay(new Date()));
      if (daysUntil <= 21) {
        nextItems.push({
          id: `hmrc-${nextHmrcDeadline.id}`,
          emoji: '💼',
          text:
            daysUntil < 0
              ? `${nextHmrcDeadline.title} is overdue.`
              : `${nextHmrcDeadline.title} is due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}.`,
          path: '/work?tab=self-employed',
          label: 'Work',
          score: daysUntil < 0 ? 84 : 64,
        });
      }
    }

    if (hasWorkSetup && !selfEmploymentActive && pendingWorkDeadlineCount === 0) {
      nextItems.push({
        id: 'work-deadline-start',
        emoji: '💼',
        text: 'Add your first work deadline so reviews and response dates stop living in email.',
        path: '/work?tab=rights',
        label: 'Work deadlines',
        score: 62,
      });
    }

    if (selfEmploymentActive && (!incomeLogResult.data || incomeLogResult.data.length === 0)) {
      nextItems.push({
        id: 'income-log-start',
        emoji: '🧾',
        text: 'Log your first income entry so Adulting can start reflecting your work reality.',
        path: '/work?tab=self-employed',
        label: 'Income log',
        score: 57,
      });
    }

      nextItems.sort((a, b) => b.score - a.score);
      const nextVisibleItems = nextItems.slice(0, 8);
      attentionItemsCache = {
        userId: user.id,
        items: nextVisibleItems,
        loadedAt: Date.now(),
      };
      setItems(nextVisibleItems);
      markConnectionHealthy();
    } catch (error) {
      if (isTransientConnectionError(error)) {
        reportConnectionIssue(
          error,
          'Adulting could not refresh your priority list because the connection dropped. Your last loaded details are still on screen.',
        );
      } else {
        console.error('Could not refresh attention items', error);
      }
      if (attentionItemsCache?.userId !== user.id) {
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [
    data.budgetCategoryPreferences,
    data.budgetLimits,
    data.expenses,
    data.profile.adultDependants,
    data.profile.annualIncome,
    data.profile.breastScreeningRelevant,
    data.profile.children,
    data.profile.dateOfBirth,
    data.profile.dependantTypes,
    data.profile.employmentType,
    data.profile.hasCervix,
    data.profile.partnerName,
    data.profile.prostateScreeningRelevant,
    data.profile.screeningInvitationPath,
    data.profile.workType,
    markConnectionHealthy,
    reportConnectionIssue,
    user,
  ]);

  useEffect(() => {
    loadAttentionItems();
  }, [loadAttentionItems]);

  useEffect(() => {
    const handleRefresh = () => {
      loadAttentionItems();
    };

    window.addEventListener('focus', handleRefresh);
    window.addEventListener('adulting:data-changed', handleRefresh);

    return () => {
      window.removeEventListener('focus', handleRefresh);
      window.removeEventListener('adulting:data-changed', handleRefresh);
    };
  }, [loadAttentionItems]);

  return {
    items,
    hasItems: items.length > 0,
    loading,
    refreshAttentionItems: loadAttentionItems,
  };
}
